import { getItem } from "data/local-queries";
import type { LocaleType } from "lib/apollo/types";
import type { ReactNode } from "react";
import React from "react";
import type { NotificationCodeEnum } from "@shared/graphql.types";
import { BannerCopy, ContactLink } from "./NotificationsBanner.styles";
import { contactMemberHappinessLink } from "@shared/constants.urls";

type NotificationDataType = {
  errorHeading?: string;
  errorMessage?: string;
  helpLink?: ReactNode;
};

export const getItemNamesString = (
  itemIds: string[],
  pimIds: string[]
): string => {
  if ((!itemIds || !itemIds.length) && pimIds.length) {
    return `<i>${pimIds.join(", ")}</i>`;
  }

  const itemNames = itemIds
    ?.map((id, i) => {
      const item = getItem(id);

      if (!item && pimIds?.length) return pimIds[i];

      if (item.type === "product") {
        return (
          item?.variant?.title ||
          item?.variant?.brandName ||
          item?.variant?.subTitle
        );
      }
      if (item.type === "membership" || item.type === "giftMembership") {
        return item?.membership?.name;
      }

      return "";
    })
    .join(", ");

  return `<i>${itemNames}</i>`;
};

type Params = {
  pimIds?: string[];
  itemIds?: string[];
  productName?: string;
  maxQty?: number;
};

export const getNotificationData = (
  locale: LocaleType,
  code: NotificationCodeEnum,
  params?: Params
): NotificationDataType => {
  const pluraliseItems = params?.pimIds && params?.pimIds?.length > 1;

  let itemNamesString = "";
  if (params?.itemIds || params?.pimIds) {
    itemNamesString = getItemNamesString(
      params?.itemIds || [],
      params?.pimIds || []
    );
  }

  const standardHelpLink = (errorCode: string): ReactNode => {
    return (
      <BannerCopy as="span" variant="acuminRegular">
        If the error persists, please contact{" "}
        <ContactLink
          variant="acuminRegular"
          _hover={{ textDecoration: "underline !important" }}
          href={contactMemberHappinessLink(locale)}
        >
          member happiness
        </ContactLink>{" "}
        for further assistance. Error code {errorCode}
      </BannerCopy>
    );
  };

  const startShoppingLink = (errorCode: string): ReactNode => {
    return (
      <BannerCopy as="span" variant="acuminRegular">
        Head to our{" "}
        <ContactLink
          variant="acuminRegular"
          _hover={{ textDecoration: "underline !important" }}
          href="/shop/bestsellers"
        >
          Bestsellers
        </ContactLink>{" "}
        for some shopping inspiration. Error code {errorCode}
      </BannerCopy>
    );
  };

  const giftMembershipLink = (): ReactNode => {
    return (
      <BannerCopy as="p" variant="acuminRegular">
        <ContactLink
          variant="acuminRegular"
          _hover={{ textDecoration: "underline !important" }}
          href="/products/bp-plus-gift-uk"
        >
          Click Here To Gift A Membership
        </ContactLink>{" "}
      </BannerCopy>
    );
  };

  const joinNowLink = (): ReactNode => {
    return (
      <BannerCopy as="span" variant="acuminRegular">
        Head to our{" "}
        <ContactLink
          variant="acuminRegular"
          _hover={{ textDecoration: "underline !important" }}
          href="/bp/join/join-beauty-pie"
        >
          Join Page
        </ContactLink>{" "}
        to see our current membership options
      </BannerCopy>
    );
  };

  const standardHeading = "Oops, something went wrong.";

  const giftMembershipHeading = "Membership removed";

  const errorData: Record<
    string,
    Record<
      | Exclude<NotificationCodeEnum, "RAF_ADVOCATE_DISCOUNT_AVAILABLE">
      | "PAYMENT_LOAD_ERROR",
      NotificationDataType
    >
  > = {
    "en-US": {
      MEMBERSHIP_INSUFFICIENT_LIMIT: {
        errorHeading:
          "The value of items in your bag exceeds the spending limit of this membership",
        errorMessage:
          "Please choose a different membership or remove some items from your bag and try again"
      },
      VARIANT_NOT_AVAILABLE: {
        errorMessage:
          "An item has been removed from your basket as it is no longer for sale."
      },
      MEMBERSHIP_NOT_AVAILABLE: {
        errorMessage:
          "A membership has been removed from your basket as it is no longer for sale.",
        helpLink: joinNowLink()
      },
      CHECKOUT_DIFFERENCE: {
        errorHeading: standardHeading,
        errorMessage:
          "One or more items were removed from your basket because they are not available for purchase.",
        helpLink: standardHelpLink("DIFF01")
      },
      CHECKOUT_EMPTY_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: "Items in your basket are unavailable for purchase.",
        helpLink: startShoppingLink("EM01")
      },
      CHECKOUT_UNKNOWN_FAILURE: {
        errorHeading: standardHeading,
        errorMessage:
          "Something is not right with one of the items in your basket.",
        helpLink: standardHelpLink("UNK01")
      },
      CHECKOUT_WISHLIST_UPDATE_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: "There was an error updating your wishlist",
        helpLink: standardHelpLink("WISH01")
      },
      CHECKOUT_SYNC_FAILURE: {
        errorHeading: standardHeading,
        errorMessage:
          "Something is not right with one of the items in your basket.",
        helpLink: standardHelpLink("SYNC01")
      },
      PAYMENT_LOAD_ERROR: {
        errorHeading: standardHeading,
        errorMessage:
          "An error occurred during loading, please refresh the page and try again.",
        helpLink: standardHelpLink("PAY05")
      },
      CHECKOUT_DISCOUNT_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: "There was an error updating your discounts",
        helpLink: standardHelpLink("DIS01")
      },
      BAD_REQUEST: {
        errorHeading: standardHeading,
        errorMessage:
          "Something is not right with one of the products in your basket.",
        helpLink: standardHelpLink("BAD_REQ")
      },
      INVALID_ZIP_OR_STATE: {
        errorHeading: standardHeading,
        errorMessage:
          "We couldn’t validate your address. Please check the zip code and/or state and try again.",
        helpLink: standardHelpLink("ZIP01")
      },
      ADDRESS_NON_DELIVERABLE: {
        errorHeading: standardHeading,
        errorMessage:
          "We couldn’t validate your address. Please check it and try again.",
        helpLink: standardHelpLink("ADD01")
      },
      INVALID_ZIP_CODE: {
        errorHeading: standardHeading,
        errorMessage: "The Zip code is not valid.",
        helpLink: standardHelpLink("ZIP02")
      },
      INVALID_ZIP_CODE_FOR_STATE: {
        errorHeading: standardHeading,
        errorMessage:
          "The Zip code is not valid for the state. Please check and try again.",
        helpLink: standardHelpLink("ZIP03")
      },
      CHECKOUT_PAYMENT_REFUSED: {
        errorHeading: standardHeading,
        errorMessage:
          "Your payment was declined. Please try again or use a different payment method"
      },
      CHECKOUT_PAYMENT_FAILED: {
        errorHeading: standardHeading,
        errorMessage:
          "Sorry, we experienced a problem. Your payment card hasn't be charged. Please try again. If this persists, please try again later",
        helpLink: standardHelpLink("PAY03")
      },
      CHECKOUT_PAYMENT_CANCELLED: {
        errorHeading: standardHeading,
        errorMessage: "This payment was cancelled. Please try again",
        helpLink: standardHelpLink("PAY04")
      },
      INVALID_CITY_STATE_ZIP: {
        errorHeading: standardHeading,
        errorMessage:
          "The city, state and zip code are not valid together. Please check and try again",
        helpLink: standardHelpLink("ZIP04")
      },
      ADD_ITEMS_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: `${itemNamesString} could not be added. Please try again.`,
        helpLink: standardHelpLink("PR05")
      },
      ITEM_OOS: {
        errorHeading: standardHeading,
        errorMessage: `${
          params?.productName || itemNamesString
        } is out of stock.`,
        helpLink: standardHelpLink("PR06")
      },
      REMOVE_ITEMS_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: `There was an issue with item${
          pluraliseItems ? "s" : ""
        } ${itemNamesString} in your bag. Please try again.`,
        helpLink: standardHelpLink("PR07")
      },
      BASKET_SERVICE_UNEXPECTED_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "The update failed, please try again.",
        helpLink: standardHelpLink("PR08")
      },
      BASKET_SERVICE_BAD_REQUEST_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "The update failed, please try again.",
        helpLink: standardHelpLink("PR09")
      },
      MAX_QTY_EXCEEDED: {
        errorHeading: `Sorry, you cannot add more ${
          params?.maxQty ? `than ${params.maxQty}` : ""
        } of this item to your basket.`
      },
      FORGOT_PASSWORD_REQUEST_ERROR: {
        errorHeading: standardHeading,
        errorMessage:
          "Apologies, we have encountered a problem at our end. Try refreshing this page and entering your details again.",
        helpLink: standardHelpLink("PASS01")
      },

      PAYMENT_FAILED_ERROR: {
        errorHeading: standardHeading,
        errorMessage:
          "Something went wrong. Your payment card has not been charged, please try again",
        helpLink: standardHelpLink("PAY02")
      },

      REGISTRATION_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "There was a technical issue.",
        helpLink: standardHelpLink("REG01")
      },

      NETWORK_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "Network error. Please check your connection.",
        helpLink: standardHelpLink("NET")
      },

      USER_EXISTS_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "Looks like you already have an account.",
        helpLink: standardHelpLink("REG02")
      },
      // US is limitless so shouldn't ever get this one
      OUT_OF_LIMIT: {
        errorHeading: "Oops, looks like you have hit your spending limit.",
        errorMessage:
          "Please remove some items from your basket or select a membership with a higher spending limit <a href='/join-beauty-pie'><u>here</u></a>.",
        helpLink: standardHelpLink("OOL")
      },
      MEMBERSHIP_KIT_REMOVED: {
        errorHeading: giftMembershipHeading,
        errorMessage:
          "The membership included in the kit is only applicable for non-members.",
        helpLink: giftMembershipLink()
      },
      INVALID_EMAIL: {
        errorHeading: standardHeading,
        errorMessage:
          "The email is not valid. Please check for typos and try again.",
        helpLink: standardHelpLink("REG01")
      },
      INVALID_COUNTRY_CODE: {
        errorHeading: standardHeading,
        errorMessage:
          "Sorry, we don't deliver to your country. Please try a different address.",
        helpLink: standardHelpLink("COUNTRY01")
      },
      LIMIT_ACCESS_PASS: {
        errorHeading: standardHeading,
        errorMessage:
          "It looks like you’ve already used your Free Trial. Access member prices with a Beauty Pie+ membership."
      }
    },
    "en-GB": {
      MEMBERSHIP_INSUFFICIENT_LIMIT: {
        errorHeading:
          "The value of items in your bag exceeds the spending limit of this membership",
        errorMessage:
          "Please choose a different membership or remove some items from your bag and try again"
      },
      VARIANT_NOT_AVAILABLE: {
        errorMessage:
          "An item has been removed from your basket as it is no longer for sale."
      },
      MEMBERSHIP_NOT_AVAILABLE: {
        errorMessage:
          "A membership has been removed from your basket as it is no longer for sale.",
        helpLink: joinNowLink()
      },
      CHECKOUT_DIFFERENCE: {
        errorHeading: standardHeading,
        errorMessage:
          "One or more items were removed from your basket because they are not available for purchase.",
        helpLink: standardHelpLink("DIFF01")
      },
      CHECKOUT_EMPTY_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: "Items in your basket are unavailable for purchase.",
        helpLink: startShoppingLink("EM01")
      },
      CHECKOUT_UNKNOWN_FAILURE: {
        errorHeading: standardHeading,
        errorMessage:
          "Something is not right with one of the items in your basket.",
        helpLink: standardHelpLink("UNK01")
      },
      CHECKOUT_WISHLIST_UPDATE_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: "There was an error updating your wishlist",
        helpLink: standardHelpLink("WISH01")
      },
      CHECKOUT_SYNC_FAILURE: {
        errorHeading: standardHeading,
        errorMessage:
          "Something is not right with one of the items in your basket.",
        helpLink: standardHelpLink("SYNC01")
      },
      PAYMENT_LOAD_ERROR: {
        errorHeading: standardHeading,
        errorMessage:
          "An error occurred during loading, please refresh the page and try again.",
        helpLink: standardHelpLink("PAY05")
      },
      CHECKOUT_DISCOUNT_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: "There was an error updating your discounts",
        helpLink: standardHelpLink("DIS01")
      },
      BAD_REQUEST: {
        errorHeading: standardHeading,
        errorMessage:
          "Something is not right with one of the products in your basket.",
        helpLink: standardHelpLink("BAD_REQ")
      },
      INVALID_ZIP_OR_STATE: {
        errorHeading: standardHeading,
        errorMessage:
          "We couldn’t validate your address. Please check the zip code and/or state and try again.",
        helpLink: standardHelpLink("ZIP01")
      },
      ADDRESS_NON_DELIVERABLE: {
        errorHeading: standardHeading,
        errorMessage:
          "We couldn’t validate your address. Please check it and try again.",
        helpLink: standardHelpLink("ADD01")
      },
      INVALID_ZIP_CODE: {
        errorHeading: standardHeading,
        errorMessage: "The Zip code is not valid.",
        helpLink: standardHelpLink("ZIP02")
      },
      INVALID_ZIP_CODE_FOR_STATE: {
        errorHeading: standardHeading,
        errorMessage:
          "The Zip code is not valid for the state. Please check and try again.",
        helpLink: standardHelpLink("ZIP03")
      },
      CHECKOUT_PAYMENT_REFUSED: {
        errorHeading: standardHeading,
        errorMessage:
          "Your payment was declined. Please try again or use a different payment method"
      },
      CHECKOUT_PAYMENT_FAILED: {
        errorHeading: standardHeading,
        errorMessage:
          "Sorry, we experienced a problem. Your payment card hasn't be charged. Please try again. If this persists, please try again later",
        helpLink: standardHelpLink("PAY03")
      },
      CHECKOUT_PAYMENT_CANCELLED: {
        errorHeading: standardHeading,
        errorMessage: "This payment was cancelled. Please try again",
        helpLink: standardHelpLink("PAY04")
      },
      INVALID_CITY_STATE_ZIP: {
        errorHeading: standardHeading,
        errorMessage:
          "The city, state and zip code are not valid together. Please check and try again",
        helpLink: standardHelpLink("ZIP04")
      },
      ADD_ITEMS_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: `${itemNamesString} could not be added. Please try again.`,
        helpLink: standardHelpLink("PR05")
      },
      ITEM_OOS: {
        errorHeading: "",
        errorMessage: `${
          params?.productName || itemNamesString
        } is out of stock and has been removed from your basket.`,
        helpLink: ""
      },
      REMOVE_ITEMS_FAILURE: {
        errorHeading: standardHeading,
        errorMessage: `There was an issue with item${
          pluraliseItems ? "s" : ""
        } ${itemNamesString} in your bag. Please try again.`,
        helpLink: standardHelpLink("PR07")
      },
      BASKET_SERVICE_UNEXPECTED_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "The update failed, please try again.",
        helpLink: standardHelpLink("PR08")
      },
      BASKET_SERVICE_BAD_REQUEST_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "The update failed, please try again.",
        helpLink: standardHelpLink("PR09")
      },
      MAX_QTY_EXCEEDED: {
        errorHeading: `Sorry, you cannot add more ${
          params?.maxQty ? `than ${params.maxQty}` : ""
        } of this item to your basket.`
      },
      FORGOT_PASSWORD_REQUEST_ERROR: {
        errorHeading: standardHeading,
        errorMessage:
          "Apologies, we have encountered a problem at our end. Try refreshing this page and entering your details again.",
        helpLink: standardHelpLink("PASS01")
      },
      PAYMENT_FAILED_ERROR: {
        errorHeading: standardHeading,
        errorMessage:
          "Something went wrong. Your payment card has not been charged, please try again",
        helpLink: standardHelpLink("PAY02")
      },
      REGISTRATION_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "There was a technical issue.",
        helpLink: standardHelpLink("REG01")
      },
      NETWORK_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "Network error. Please check your connection.",
        helpLink: standardHelpLink("NET")
      },
      USER_EXISTS_ERROR: {
        errorHeading: standardHeading,
        errorMessage: "Looks like you already have an account.",
        helpLink: standardHelpLink("REG02")
      },
      MEMBERSHIP_KIT_REMOVED: {
        errorHeading: giftMembershipHeading,
        errorMessage:
          "The membership included in the kit is only applicable for non-members.",
        helpLink: giftMembershipLink()
      },
      OUT_OF_LIMIT: {
        errorHeading:
          "You’ve hit your maximum spending limit. Here’s your FREE top-up!",
        errorMessage:
          "To give you a little lift, we’d love to give you a free £1000 spending limit boost – effectively immediately – by clicking <a href='/basket?items=[t:3074457345616730272:1]'><u>here</u></a>. Happy shopping!"
      },
      INVALID_EMAIL: {
        errorHeading: standardHeading,
        errorMessage:
          "The email is not valid. Please check for typos and try again.",
        helpLink: standardHelpLink("REG01")
      },
      INVALID_COUNTRY_CODE: {
        errorHeading: standardHeading,
        errorMessage:
          "Sorry, we don't deliver to your country. Please try a different address.",
        helpLink: standardHelpLink("COUNTRY01")
      },
      LIMIT_ACCESS_PASS: {
        errorHeading: standardHeading,
        errorMessage:
          "It looks like you’ve already used your Free Trial. Access member prices with a Beauty Pie+ membership."
      }
    }
  };

  if (!errorData[locale][code]) {
    console.error(`Notification code not recognised: ${code}`);
    return {
      errorHeading: standardHeading,
      errorMessage: "",
      helpLink: standardHelpLink("GEN01")
    };
  }

  return errorData[locale][code];
};
